import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch } from "../../../../app/hooks";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import useNotification from "../../../../hooks/useNotification";
import { EezyCompany } from "../../../invoices/invoiceAPI";
import { fetchEezyCompanies } from "../../../invoices/invoiceSlice";
import { Payslip } from "../../../salaries/payslips/payslipAPI";
import {
  createManualPayslip,
  updatePayslip,
} from "../../../salaries/payslips/payslipsSlice";
import { ManualPayslipDialogContent } from "./ManualPayslipDialogContent";

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
  talentId?: string;
  payslip?: Payslip | null;
};

type FormValues = {
  eezyCompanyId: string;
  startDate: Date | null;
  endDate: Date | null;
  salaryDate: Date | null;
  valueDate: Date | null;
};

export const ManualPayslipDialog = ({
  isOpen,
  closeDialog,
  talentId,
  payslip,
}: Props) => {
  const dispatch = useAppDispatch();
  const { sendNotification } = useNotification();
  const { t } = useTranslation();

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );

  const defaultValues: FormValues = {
    eezyCompanyId: "",
    startDate: null,
    endDate: null,
    salaryDate: null,
    valueDate: null,
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const close = () => {
    methods.reset(defaultValues);
    closeDialog();
  };

  const handleSave = () => {
    if (!talentId) return;
    const form = methods.getValues();
    if (!payslip) {
      const requestPayload = {
        periodStart: format(new Date(form.startDate!!), isoFormat),
        periodEnd: format(new Date(form.endDate!!), isoFormat),
        salaryDate: format(new Date(form.salaryDate!!), isoFormat),
        valueDate: format(new Date(form.valueDate!!), isoFormat),
        talentId,
        eezyCompanyId: form.eezyCompanyId,
      };
      dispatch(createManualPayslip(requestPayload)).then((action) => {
        if (isFulfilled(action)) {
          if (action.payload.length === 0) {
            sendNotification(
              t("payslips:payslipCreationFailed"),
              t("payslips:noEntriesFound"),
            );
          }
          close();
        }
      });
    } else {
      const requestPayload = {
        payslipId: payslip.id,
        salaryDate: format(new Date(form.salaryDate!!), isoFormat),
        valueDate: format(new Date(form.valueDate!!), isoFormat),
      };
      dispatch(updatePayslip(requestPayload)).then((action) => {
        if (isFulfilled(action)) {
          close();
        }
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchEezyCompanies({})).then(
        (action) =>
          isFulfilled(action) && setEezyCompanyOptions(action.payload),
      );
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (payslip) {
      methods.reset({
        eezyCompanyId: payslip.eezyCompanyId,
        startDate: new Date(payslip.periodStart),
        endDate: new Date(payslip.periodEnd),
        salaryDate: new Date(payslip.salaryDate),
        valueDate: new Date(payslip.valueDate),
      });
    }
  }, [payslip, methods]);

  return (
    <DialogComponent open={isOpen} onClose={close}>
      <FormProvider {...methods}>
        <ManualPayslipDialogContent
          eezyCompanies={eezyCompanyOptions}
          closeDialog={close}
          handleSave={handleSave}
          isNewPayslip={!payslip}
        />
      </FormProvider>
    </DialogComponent>
  );
};
